import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Clock, ShieldCheck, MessageCircle, ArrowRight } from 'lucide-react';
import { SITE_CONFIG } from '../config/siteConfig';

interface MaintenancePageProps {
    onOpenAdmin?: () => void;
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({ onOpenAdmin }) => {
    const handleUrgentWhatsApp = () => {
        const phone = SITE_CONFIG.contact?.whatsapp || '50379225906';
        const formattedPhone = phone.replace(/[^0-9]/g, '');
        const message = encodeURIComponent('Hola Guzmán / CarbonCase SV, veo que el catálogo web está en mantenimiento. Deseo consultar stock y hacer un pedido urgente.');
        window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
    };

    return (
        <div className="min-h-screen bg-black text-white flex flex-col justify-between relative px-4 py-12 overflow-hidden select-none font-sans">
            {/* Hexagonal / Carbon Weave Background Texture */}
            <div className="absolute inset-0 bg-hex-pattern opacity-10 pointer-events-none" />
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-white/[0.02] rounded-full blur-[160px] pointer-events-none" />

            {/* Header with Logo */}
            <header className="max-w-7xl w-full mx-auto flex items-center justify-between z-10 relative">
                <img
                    src={SITE_CONFIG.logo}
                    alt={SITE_CONFIG.brandName}
                    className="h-10 md:h-12 w-auto object-contain"
                />

                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-[11px] font-mono text-zinc-400">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span>Mantenimiento Activo</span>
                </div>
            </header>

            {/* Main Central Content */}
            <main className="max-w-2xl w-full mx-auto my-auto text-center relative z-10 space-y-8 py-12">
                {/* Status Badge */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-900/90 border border-zinc-800 text-xs font-mono uppercase tracking-widest text-zinc-300 backdrop-blur-md"
                >
                    <Wrench className="w-3.5 h-3.5 text-white" />
                    <span>Actualización de Infraestructura & Inventario</span>
                </motion.div>

                {/* Main Titles */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="space-y-4"
                >
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-black uppercase italic tracking-tighter text-white drop-shadow-xl leading-tight">
                        Optimizando Nuestro <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-300 to-zinc-600">
                            Catálogo Premium
                        </span>
                    </h1>
                    <p className="text-zinc-400 text-sm sm:text-base md:text-lg max-w-xl mx-auto leading-relaxed">
                        Estamos sincronizando nuevas piezas de fibra de carbono real y preparando mejoras de rendimiento. Volveremos a estar en línea muy pronto.
                    </p>
                </motion.div>

                {/* Emergency Order Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 md:p-8 backdrop-blur-sm max-w-lg mx-auto text-left space-y-5"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 text-emerald-400">
                            <MessageCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-white font-display font-bold uppercase text-sm tracking-wide">
                                ¿Necesitas un case de urgencia?
                            </h2>
                            <p className="text-zinc-400 text-xs mt-1 leading-relaxed">
                                Nuestras líneas directas de WhatsApp y entregas a domicilio en El Salvador siguen operando con normalidad.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleUrgentWhatsApp}
                        className="w-full bg-[#25D366] hover:bg-[#20ba59] text-black font-display font-black uppercase text-xs md:text-sm tracking-widest py-4 px-6 rounded-lg flex items-center justify-center gap-3 transition-all shadow-[0_0_25px_rgba(37,211,102,0.3)] hover:scale-[1.01] active:scale-[0.99]"
                    >
                        <span>Pedir Asesoría Directa por WhatsApp</span>
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </motion.div>

                {/* Features & Assurance */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="grid grid-cols-2 gap-4 max-w-md mx-auto pt-2"
                >
                    <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs">
                        <ShieldCheck className="w-4 h-4 text-zinc-400" />
                        <span>Garantía de Fábrica 12M</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs">
                        <Clock className="w-4 h-4 text-zinc-400" />
                        <span>Envíos Activos a Todo El Salvador</span>
                    </div>
                </motion.div>
            </main>

            {/* Footer with Admin Access */}
            <footer className="max-w-7xl w-full mx-auto border-t border-zinc-900 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-600 relative z-10">
                <p>© {new Date().getFullYear()} {SITE_CONFIG.brandName}. Todos los derechos reservados.</p>
                
                {onOpenAdmin && (
                    <button
                        onClick={onOpenAdmin}
                        className="text-zinc-600 hover:text-zinc-400 uppercase tracking-widest text-[10px] transition-colors"
                    >
                        Acceso Administrativo
                    </button>
                )}
            </footer>
        </div>
    );
};

export default MaintenancePage;
